import React, { useEffect, useRef, useState } from 'react';

import { Button, primaryTheme } from '@repo/ui/components/Buttons';
import { validators } from '@repo/ui/components/Inputs';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, H3, P2 } from '@repo/ui/components/Typography';
import { Logo } from '@repo/ui/deprecated/Logo';
import { useNewThemeActive } from '@repo/ui/theming';
import { HELP_PAGE_URL } from '@repo/utils/url';
import hasIn from 'lodash/hasIn';

import userApi from '../../../api/user';
import TextInput from '../../TextInput/TextInput';
import { SwitchModeLabel } from '../Authentication/styles';

import { Content } from './styles';
import { ForgotPasswordProps } from './types';

const forgotPasswordMessages: Record<string, string> = {
  INVALID_CHALLENGE_SOLUTION: 'Something went wrong. Please try again',
  WRONG_CHALLENGE_SOLUTION: 'Something went wrong. Please try again',
};

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onGoBack, pow }) => {
  const isNewThemeActive = useNewThemeActive();

  const email = useRef<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [allowSubmit, setAllowSubmit] = useState(true);
  const { fetchChallenge, challenge, isChallengeReady } = pow;

  const commit = (text: string): void => {
    email.current = text;

    const error = validators.email(text) ?? '';
    setError(error);
  };

  useEffect(() => {
    if (!submitted && allowSubmit && error) {
      fetchChallenge();
    }
  }, [submitted, allowSubmit, error, fetchChallenge]);

  const submit = async (): Promise<void> => {
    if (error) {
      return;
    }
    setAllowSubmit(false);
    try {
      const response = await userApi.requestPasswordResetCode(
        email.current!,
        challenge
      );
      if (response.error) {
        if (hasIn(forgotPasswordMessages, response.error)) {
          setError(forgotPasswordMessages[response.error]);
        } else {
          setError(response.error);
        }
      } else {
        setSubmitted(true);
      }
    } catch (e) {
      setError('Failed to reset password.');
    }
    setAllowSubmit(true);
  };

  const renderNotSubmitted = (): JSX.Element => (
    <>
      <P2>Please enter your e-mail address to reset your password.</P2>
      <Spacer height="24px" />
      <TextInput
        autoComplete="username"
        error={error}
        name="email"
        placeholder="E-Mail"
        onChanging={(email: string): void => commit(email)}
        onEnter={submit}
      />
      <Spacer height={error ? '8px' : '12px'} />
      <Button
        disabled={
          !allowSubmit || error === undefined || !!error || !isChallengeReady
        }
        height="40px"
        label="Continue"
        name="continueForgotPasswordButton"
        theme={primaryTheme}
        width="100%"
        onClick={submit}
      />
      <Spacer height="8px" />
      <SwitchModeLabel
        noDecoration
        as="label"
        isNewThemeActive={isNewThemeActive}
        onClick={onGoBack}
      >
        Back
      </SwitchModeLabel>
    </>
  );

  const renderSubmitted = (): JSX.Element => (
    <>
      <P2>We sent you an email with password reset instructions</P2>
      <Spacer height="24px" />
      <P2>
        Didn't receive the email?
        <br />
        Check your spam folder or contact&nbsp;
        <a href={HELP_PAGE_URL}>Support</a>.
        <Spacer height="24px" />
        <Button
          height="40px"
          label="Back to Sign In"
          name="backToSignInButton"
          theme={primaryTheme}
          width="100%"
          onClick={onGoBack}
        />
      </P2>
    </>
  );

  const Text = isNewThemeActive ? H3 : H2;

  return (
    <Content>
      <Logo height={40} />
      <Spacer height="24px" />
      <Text>Forgot your Password?</Text>
      <Spacer height="8px" />
      {submitted ? renderSubmitted() : renderNotSubmitted()}
    </Content>
  );
};

export default ForgotPassword;
